import { GraphQLError } from 'graphql'
import { ApolloError } from '@apollo/client'
import { differenceInDays, min, max, addDays } from 'date-fns'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorMessage = (error: any): string => {
  if (error && Array.isArray(error)) {
    return error.map((e) => getErrorMessage(e)).join(', ')
  }

  if (error && error instanceof GraphQLError) {
    return error.message
  }

  if (error && error instanceof ApolloError) {
    const clientErrors = error.clientErrors?.map((e) => e.message) ?? []
    const graphQlErrors = error.graphQLErrors?.map((e) => e.message) ?? []
    const networkError = error.networkError?.message

    const errors = [...clientErrors, ...graphQlErrors, networkError].filter((e) => e)

    return errors.join(', ')
  }
  if (error && error instanceof Error) {
    return error.message
  }
  if (error?.message && typeof error.message === 'string') {
    return error.message
  }
  if (error && typeof error === 'string') {
    return error
  }
  return 'Unexpected error'
}

export function dateAtStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function getDayDiff(start: Date, end: Date): number {
  const startDate = dateAtStartOfDay(start)
  const endDate = dateAtStartOfDay(end)
  const msInDay = 24 * 60 * 60 * 1000
  return Math.ceil(Math.abs(Number(endDate) - Number(startDate)) / msInDay)
}

export function range(start: number, end: number) {
  return Array.from(Array(end - start + 1).keys()).map((i) => i + start)
}

export function getNightsInRange(startDate: Date, endDate: Date): Date[] {
  const date = new Date(startDate.getTime())

  const dates: Date[] = []

  while (date < endDate) {
    dates.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }

  return dates
}

export function dateEquality(firstDate: Date, secondDate: Date) {
  return firstDate.getFullYear() === secondDate.getFullYear() && firstDate.getMonth() === secondDate.getMonth() && firstDate.getDate() === secondDate.getDate()
}

/**
 * Calculates the number of overlapping days between a validity period and a reservation period.
 *
 * ```
 * Example1: validity = {start: 2020-01-01, end: 2020-01-10} reservation = {start: 2020-01-01, end: 2020-01-10} sovrapposizione = 9 days
 * Example2: validity = {start: 2020-01-01, end: 2020-01-10} reservation = {start: 2020-01-03, end: 2020-01-07} sovrapposizione = 4 days
 * Example3: validity = {start: 2020-02-01, end: 2020-02-10} reservation = {start: 2020-01-25, end: 2020-02-01} sovrapposizione = 0 days
 * Example4: validity = {start: 2020-02-01, end: 2020-02-10} reservation = {start: 2020-01-25, end: 2020-02-02} sovrapposizione = 1 day
 * Example5: validity = {start: 2020-02-01, end: 2020-02-10} reservation = {start: 2020-02-11, end: 2020-02-15} sovrapposizione = 0 days
 * Example6: validity = {start: 2020-02-01, end: 2020-02-10} reservation = {start: 2020-02-10, end: 2020-02-15} sovrapposizione = 1 day
 * ```
 *
 * @param validity - The validity period with start and/or end dates.
 * @param {Date} [validity.start] - The start date of the validity period.
 * @param {Date} [validity.end] - The end date of the validity period.
 * @param reservation - The reservation period with start and end dates.
 * @param {Date} reservation.start - The start date of the reservation period.
 * @param {Date} reservation.end - The end date of the reservation period.
 * @returns {number} The number of overlapping days. Returns 0 if there is no overlap.
 */
export const calculateReservationOverlapDays = (validity: { start?: Date | null; end?: Date | null }, reservation: { start: Date; end: Date }): number => {
  const validityStart = validity.start ?? reservation.start
  const validityEnd = validity.end ?? reservation.end
  const overlapStart = max([validityStart, reservation.start])
  const overlapEnd = min([addDays(validityEnd, 1), reservation.end])
  const overlap = differenceInDays(overlapEnd, overlapStart)
  return overlap > 0 ? overlap : 0
}
